"use client";

import { useCallback, useMemo, createContext, useContext, useState, useEffect, useSyncExternalStore, ReactNode } from "react";
import { AudioEngine } from "../AudioEngine";
import { AudioEngineState, AudioEngineTickState, TrackState, RegionState, AudioInputDevice, PendingRecording, WaveformData, LoadingProgress } from "../types";
import { useEngineSelector, SelectorStoreConfig } from "./useEngineSelector";
import { posthog, AnalyticsEvent } from "@src/analytics";

// ============================================================================
// Hydration
// ============================================================================

/**
 * Context for tracking hydration state globally.
 * Once the app hydrates, all components should immediately get true.
 */
export const HydratedContext = createContext<boolean>(false);

/**
 * Provider component that tracks hydration state globally.
 * Should be placed near the root of the app, wrapping all components
 * that need to know about hydration state.
 *
 * This is exported so apps can place it in their provider tree.
 * The audio hooks will only return real engine state after hydration
 * to avoid SSR/hydration mismatches.
 */
const emptySubscribe = () => () => {};
const getClientSnapshot = () => true;
const getServerSnapshot = () => false;
export function HydratedProvider({
  children
}: {
  children: ReactNode;
}) {
  const hydrated = useSyncExternalStore(emptySubscribe, getClientSnapshot, getServerSnapshot);
  return <HydratedContext.Provider value={hydrated} data-sentry-element="HydratedContext.Provider" data-sentry-component="HydratedProvider" data-sentry-source-file="hooks.tsx">{children}</HydratedContext.Provider>;
}

/**
 * Hook to check if the app has hydrated.
 * Returns false during SSR and initial hydration, true after.
 */
export function useHydrated(): boolean {
  return useContext(HydratedContext);
}

// ============================================================================
// Context
// ============================================================================

export const AudioEngineContext = createContext<AudioEngine | null>(null);

/**
 * Default state used for server-side rendering and initial hydration.
 * This ensures server and client render the same initial values,
 * avoiding hydration mismatches for persisted state loaded from IndexedDB.
 */
export const DEFAULT_ENGINE_STATE: AudioEngineState = {
  ready: false,
  playing: false,
  seeking: false,
  duration: 0,
  masterGain: 0.8,
  tracks: {},
  regions: {},
  waveforms: {},
  recording: false,
  recordingStartPosition: null,
  selectedInputDeviceId: null,
  inputMonitoring: false,
  inputAudioMonitoring: false,
  inputChannelCount: null,
  selectedInputChannel: 'stereo',
  recordingTargetTrackId: null,
  deletedRegionIds: [],
  playbackRate: 1.0,
  transpose: 0,
  loadingProgress: {
    total: 0,
    loaded: 0,
    error: null
  }
};

/**
 * Default tick state used for server-side rendering and initial hydration.
 * Tick state contains high-frequency values that update ~60 times/sec during playback.
 */
export const DEFAULT_TICK_STATE: AudioEngineTickState = {
  currentTimestamp: 0,
  inputLevel: 0,
  inputLevelRight: 0,
  recordingWaveform: null
};

/**
 * Get the AudioEngine instance from context.
 * Must be used within an AudioEngineContext.Provider.
 *
 * @example
 * ```tsx
 * const engine = useAudioEngine();
 * engine.play();
 * ```
 */
export function useAudioEngine(): AudioEngine {
  const engine = useContext(AudioEngineContext);
  if (!engine) {
    throw new Error("useAudioEngine must be used within an AudioEngineContext.Provider");
  }
  return engine;
}

// ============================================================================
// Core Hooks
// ============================================================================

/**
 * Subscribe to a selected portion of AudioEngine state with automatic memoization.
 * Only re-renders when the selected value changes (using shallow comparison for objects).
 *
 * This is the recommended way to subscribe to engine state for optimal performance.
 *
 * During SSR and initial hydration, returns values from DEFAULT_ENGINE_STATE to avoid
 * hydration mismatches (persisted state is loaded from IndexedDB on client only).
 *
 * The selector receives both state and engine, so you can access engine methods
 * without needing a separate useAudioEngine() call.
 *
 * @example
 * ```tsx
 * // Only re-renders when `playing` changes
 * const playing = useAudioEngineState(s => s.playing);
 *
 * // Select multiple values (re-renders when any change)
 * const { playing, seeking } = useAudioEngineState(s => ({
 *   playing: s.playing,
 *   seeking: s.seeking,
 * }));
 *
 * // Access engine methods directly
 * const isPending = useAudioEngineState((s, e) => e.hasTrackPendingRegions(trackId));
 * ```
 */
export function useAudioEngineState<T>(selector: (state: AudioEngineState, engine: AudioEngine) => T): T {
  const engine = useAudioEngine();
  const hydrated = useHydrated();
  const config = useMemo((): SelectorStoreConfig<AudioEngineState> => ({
    getSnapshot: engine.getSnapshot,
    subscribe: engine.subscribe,
    defaultState: DEFAULT_ENGINE_STATE
  }), [engine]);

  // Wrap selector to include engine parameter
  const wrappedSelector = useCallback((state: AudioEngineState) => selector(state, engine), [selector, engine]);
  return useEngineSelector(config, wrappedSelector, hydrated);
}

/**
 * Subscribe to a selected portion of AudioEngine tick state with automatic memoization.
 * Tick state contains high-frequency values (currentTimestamp, inputLevel, recordingWaveform)
 * that update ~60 times/sec during playback.
 *
 * Use this hook for components that need timestamp, input levels, or recording waveform.
 * Components that don't need these values should use useAudioEngineState instead
 * to avoid unnecessary re-renders during playback.
 *
 * @example
 * ```tsx
 * // Get current timestamp (updates every frame during playback)
 * const currentTimestamp = useAudioEngineTicker(s => s.currentTimestamp);
 *
 * // Get input levels for level meter
 * const { inputLevel, inputLevelRight } = useAudioEngineTicker(s => ({
 *   inputLevel: s.inputLevel,
 *   inputLevelRight: s.inputLevelRight,
 * }));
 *
 * // Get recording waveform
 * const recordingWaveform = useAudioEngineTicker(s => s.recordingWaveform);
 * ```
 */
export function useAudioEngineTicker<T>(selector: (state: AudioEngineTickState, engine: AudioEngine) => T): T {
  const engine = useAudioEngine();
  const hydrated = useHydrated();
  const config = useMemo((): SelectorStoreConfig<AudioEngineTickState> => ({
    getSnapshot: engine.getTickSnapshot,
    subscribe: engine.subscribeTick,
    defaultState: DEFAULT_TICK_STATE
  }), [engine]);

  // Wrap selector to include engine parameter
  const wrappedSelector = useCallback((state: AudioEngineTickState) => selector(state, engine), [selector, engine]);
  return useEngineSelector(config, wrappedSelector, hydrated);
}

// ============================================================================
// Convenience Hooks
// ============================================================================

/**
 * Transport state (excludes currentTimestamp for performance).
 * Re-renders when playing, seeking, or duration changes.
 *
 * For currentTimestamp, use useCurrentTimestamp() separately - this allows
 * components that don't need high-frequency timestamp updates to avoid
 * re-rendering on every animation frame.
 */
export interface TransportState {
  playing: boolean;
  seeking: boolean;
  duration: number;
  ready: boolean;
  canPlay: boolean;
}
export interface TransportControls {
  play: () => void;
  pause: () => void;
  stop: () => void;
  seek: (timestamp: number) => Promise<void>;
  seekDelta: (delta: number) => void;
  seekPercentage: (percentage: number) => void;
}
export interface UseTransportResult extends TransportState, TransportControls {}

/**
 * Hook for transport state and controls.
 * Optimized to only re-render when transport-related state changes.
 *
 * Note: Does NOT include currentTimestamp to avoid re-renders on every tick.
 * Use useCurrentTimestamp() separately for components that need it.
 *
 * @example
 * ```tsx
 * const { playing, duration, play, pause, seek } = useTransport();
 * // If you need timestamp:
 * const currentTimestamp = useCurrentTimestamp();
 * ```
 */
export function useTransport(): UseTransportResult {
  const engine = useAudioEngine();
  const state = useAudioEngineState(s => ({
    playing: s.playing,
    seeking: s.seeking,
    duration: s.duration,
    ready: s.ready,
    canPlay: s.ready && Object.keys(s.regions).length > 0
  }));

  // Controls are stable references (engine methods are bound)
  const controls: TransportControls = {
    play: useCallback(() => {
      engine.play();
      posthog.capture(AnalyticsEvent.SONG_PLAYED);
    }, [engine]),
    pause: useCallback(() => {
      engine.pause();
      posthog.capture(AnalyticsEvent.SONG_PAUSED);
    }, [engine]),
    stop: useCallback(() => {
      engine.stop();
      posthog.capture(AnalyticsEvent.SONG_STOPPED);
    }, [engine]),
    seek: useCallback((timestamp: number) => engine.seek(timestamp), [engine]),
    seekDelta: useCallback((delta: number) => engine.seekDelta(delta), [engine]),
    seekPercentage: useCallback((pct: number) => engine.seekPercentage(pct), [engine])
  };
  return {
    ...state,
    ...controls
  };
}

/**
 * Hook for current timestamp only.
 * Use this for components that need high-frequency timestamp updates (e.g., progress bars).
 *
 * @example
 * ```tsx
 * const currentTimestamp = useCurrentTimestamp();
 * ```
 */
export function useCurrentTimestamp(): number {
  return useAudioEngineTicker(s => s.currentTimestamp);
}

/**
 * Hook for a single track's state.
 * Returns undefined if track doesn't exist.
 *
 * @example
 * ```tsx
 * const trackState = useTrackState('track-1');
 * if (trackState) {
 *   console.log(trackState.muted, trackState.solo);
 * }
 * ```
 */
export function useTrackState(trackId: string): TrackState | undefined {
  return useAudioEngineState(s => s.tracks[trackId]);
}

/**
 * Hook for track controls.
 * Returns stable function references for controlling a track.
 *
 * @example
 * ```tsx
 * const { toggleMute, toggleSolo, setGain } = useTrackControls('track-1');
 * ```
 */
export interface TrackControls {
  toggleMute: () => void;
  toggleSolo: () => void;
  setGain: (gain: number) => void;
}
export function useTrackControls(trackId: string): TrackControls {
  const engine = useAudioEngine();
  return {
    toggleMute: useCallback(() => engine.toggleMute(trackId), [engine, trackId]),
    toggleSolo: useCallback(() => engine.toggleSolo(trackId), [engine, trackId]),
    setGain: useCallback((gain: number) => engine.setTrackGain(trackId, gain), [engine, trackId])
  };
}

/**
 * Hook for master gain state and control.
 *
 * @example
 * ```tsx
 * const { masterGain, setMasterGain } = useMasterGain();
 * ```
 */
export interface MasterGainResult {
  masterGain: number;
  setMasterGain: (gain: number) => void;
}
export function useMasterGain(): MasterGainResult {
  const engine = useAudioEngine();
  const masterGain = useAudioEngineState(s => s.masterGain);
  const setMasterGain = useCallback((gain: number) => engine.setMasterGain(gain), [engine]);
  return {
    masterGain,
    setMasterGain
  };
}

/**
 * Hook for playback rate control.
 *
 * @example
 * ```tsx
 * const { playbackRate, setPlaybackRate } = usePlaybackRate();
 * ```
 */
export interface PlaybackRateResult {
  playbackRate: number;
  setPlaybackRate: (rate: number) => void;
}
export function usePlaybackRate(): PlaybackRateResult {
  const engine = useAudioEngine();
  const playbackRate = useAudioEngineState(s => s.playbackRate);
  const setPlaybackRate = useCallback((rate: number) => engine.setPlaybackRate(rate), [engine]);
  return {
    playbackRate,
    setPlaybackRate
  };
}

/**
 * Hook for controlling transpose (pitch shift in semitones).
 *
 * @example
 * ```tsx
 * const { transpose, setTranspose } = useTranspose();
 * ```
 */
export interface TransposeResult {
  transpose: number;
  setTranspose: (semitones: number) => void;
}
export function useTranspose(): TransposeResult {
  const engine = useAudioEngine();
  const transpose = useAudioEngineState(s => s.transpose);
  const setTranspose = useCallback((semitones: number) => engine.setTranspose(semitones), [engine]);
  return {
    transpose,
    setTranspose
  };
}

/**
 * Hook for waveform data for a specific region.
 * Returns undefined if waveform is not yet loaded.
 *
 * @example
 * ```tsx
 * const waveform = useWaveform('region-1');
 * if (waveform) {
 *   // Render waveform visualization
 * }
 * ```
 */
export function useWaveform(regionId: string): WaveformData | undefined {
  return useAudioEngineState(s => s.waveforms[regionId]);
}

/**
 * Hook for getting a combined master waveform across all tracks.
 * The engine caches this internally and only recomputes when waveforms/regions change.
 *
 * Returns null if no waveform data is available yet.
 *
 * @example
 * ```tsx
 * const masterWaveform = useMasterWaveform();
 * if (masterWaveform) {
 *   // Render combined waveform visualization
 * }
 * ```
 */
export function useMasterWaveform(): WaveformData | null {
  return useAudioEngineState((_s, engine) => engine.getMasterWaveform());
}

/**
 * Hook for ready state.
 *
 * @example
 * ```tsx
 * const ready = useEngineReady();
 * if (!ready) return <Loading />;
 * ```
 */
export function useEngineReady(): boolean {
  return useAudioEngineState(s => s.ready);
}

/**
 * Hook for loading progress state.
 * Returns the current loading progress including total regions, loaded count, and any error.
 *
 * @example
 * ```tsx
 * const { total, loaded, error } = useLoadingProgress();
 * const percent = total > 0 ? Math.round((loaded / total) * 100) : 0;
 *
 * if (error) {
 *   return <ErrorState error={error} />;
 * }
 * return <ProgressBar value={percent} />;
 * ```
 */
export function useLoadingProgress(): LoadingProgress {
  return useAudioEngineState(s => s.loadingProgress);
}

// ============================================================================
// Recording Hooks
// ============================================================================

/**
 * Recording state (read-only).
 * For controls, use useRecorder().
 */
export interface RecordingState {
  recording: boolean;
  recordingStartPosition: number | null;
  recordingTargetTrackId: string | null;
}

/**
 * Hook for recording state (read-only).
 * Use useRecorder() for controls.
 */
export function useRecording(): RecordingState {
  return useAudioEngineState(s => ({
    recording: s.recording,
    recordingStartPosition: s.recordingStartPosition,
    recordingTargetTrackId: s.recordingTargetTrackId
  }));
}

/**
 * Recording state and controls.
 */
export interface RecorderResult {
  recording: boolean;
  recordingStartPosition: number | null;
  recordingTargetTrackId: string | null;
  setRecordingTarget: (trackId: string | null) => void;
  startRecording: (targetTrackId: string) => Promise<void>;
  stopRecording: () => Promise<PendingRecording>;
  cancelRecording: () => void;
}

/**
 * Hook for recording controls.
 *
 * @example
 * ```tsx
 * const { recording, stopRecording } = useRecorder();
 *
 * // For starting recordings with full logic (permission handling, track creation, etc.),
 * // use the useStartRecording hook from @src/hooks/management/useStartRecording
 * ```
 */
export function useRecorder(): RecorderResult {
  const engine = useAudioEngine();
  const state = useAudioEngineState(s => ({
    recording: s.recording,
    recordingStartPosition: s.recordingStartPosition,
    recordingTargetTrackId: s.recordingTargetTrackId
  }));
  return {
    ...state,
    setRecordingTarget: useCallback((trackId: string | null) => engine.setRecordingTarget(trackId), [engine]),
    startRecording: useCallback((targetTrackId: string) => engine.startRecording(targetTrackId), [engine]),
    stopRecording: useCallback(() => engine.stopRecording(), [engine]),
    cancelRecording: useCallback(() => engine.cancelRecording(), [engine])
  };
}

/**
 * Input devices state and controls.
 */
export interface InputDevicesResult {
  devices: AudioInputDevice[];
  loading: boolean;
  error: Error | null;
  requestPermission: () => Promise<void>;
  refreshDevices: () => Promise<void>;
}

/**
 * Hook for managing audio input devices.
 * Handles permission requests and device enumeration.
 *
 * @example
 * ```tsx
 * const { devices, loading, requestPermission, refreshDevices } = useInputDevices();
 *
 * useEffect(() => {
 *   refreshDevices();
 * }, []);
 * ```
 */
export function useInputDevices(): InputDevicesResult {
  const engine = useAudioEngine();
  const [devices, setDevices] = useState<AudioInputDevice[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const refreshDevices = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const deviceList = await engine.getInputDevices();
      setDevices(deviceList);
    } catch (e) {
      setError(e instanceof Error ? e : new Error(String(e)));
    } finally {
      setLoading(false);
    }
  }, [engine]);
  const requestPermission = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      await engine.requestInputPermission();
      // After permission granted, refresh devices to get labels
      await refreshDevices();
    } catch (e) {
      setError(e instanceof Error ? e : new Error(String(e)));
      setLoading(false);
    }
  }, [engine, refreshDevices]);
  return {
    devices,
    loading,
    error,
    requestPermission,
    refreshDevices
  };
}

/**
 * Selected input device state and controls.
 */
export interface SelectedInputDeviceResult {
  selectedInputDeviceId: string | null;
  setSelectedInputDevice: (deviceId: string | null) => void;
}

/**
 * Hook for managing the selected input device.
 * The selected device is persisted and used when starting recording.
 *
 * @example
 * ```tsx
 * const { selectedInputDeviceId, setSelectedInputDevice } = useSelectedInputDevice();
 *
 * const handleDeviceSelect = (deviceId: string) => {
 *   setSelectedInputDevice(deviceId);
 * };
 * ```
 */
export function useSelectedInputDevice(): SelectedInputDeviceResult {
  const engine = useAudioEngine();
  const selectedInputDeviceId = useAudioEngineState(s => s.selectedInputDeviceId);
  const setSelectedInputDevice = useCallback((deviceId: string | null) => engine.setSelectedInputDevice(deviceId), [engine]);
  return {
    selectedInputDeviceId,
    setSelectedInputDevice
  };
}

/**
 * Input level monitoring state and controls.
 */
export interface InputLevelMonitorResult {
  inputMonitoring: boolean;
  inputLevel: number;
  inputLevelRight: number;
  inputChannelCount: number | null;
  startMonitoring: () => Promise<void>;
  stopMonitoring: () => void;
}

/**
 * Hook for monitoring input level from the selected device.
 * Automatically stops monitoring when the component unmounts.
 *
 * @example
 * ```tsx
 * const { inputMonitoring, inputLevel, inputLevelRight, inputChannelCount, startMonitoring, stopMonitoring } = useInputLevelMonitor();
 *
 * useEffect(() => {
 *   startMonitoring();
 *   return () => stopMonitoring();
 * }, [startMonitoring, stopMonitoring]);
 *
 * // Display level as percentage
 * const levelPercent = Math.round(inputLevel * 100);
 * ```
 */
export function useInputLevelMonitor(): InputLevelMonitorResult {
  const engine = useAudioEngine();
  const inputMonitoring = useAudioEngineState(s => s.inputMonitoring);
  const inputChannelCount = useAudioEngineState(s => s.inputChannelCount);
  // Input levels are high-frequency tick state
  const inputLevel = useAudioEngineTicker(s => s.inputLevel);
  const inputLevelRight = useAudioEngineTicker(s => s.inputLevelRight);
  const startMonitoring = useCallback(() => engine.startInputMonitoring(), [engine]);
  const stopMonitoring = useCallback(() => engine.stopInputMonitoring(), [engine]);

  // Clean up monitoring on unmount
  useEffect(() => {
    return () => {
      engine.stopInputMonitoring();
    };
  }, [engine]);
  return {
    inputMonitoring,
    inputLevel,
    inputLevelRight,
    inputChannelCount,
    startMonitoring,
    stopMonitoring
  };
}

/**
 * Input audio monitoring state and controls (hear yourself through speakers).
 */
export interface InputAudioMonitoringResult {
  inputAudioMonitoring: boolean;
  setInputAudioMonitoring: (enabled: boolean) => void;
}

/**
 * Hook for managing input audio monitoring (hear yourself through speakers).
 * This only works when input level monitoring is active.
 *
 * @example
 * ```tsx
 * const { inputAudioMonitoring, setInputAudioMonitoring } = useInputAudioMonitoring();
 *
 * // Toggle hearing yourself
 * setInputAudioMonitoring(!inputAudioMonitoring);
 * ```
 */
export function useInputAudioMonitoring(): InputAudioMonitoringResult {
  const engine = useAudioEngine();
  const inputAudioMonitoring = useAudioEngineState(s => s.inputAudioMonitoring);
  const setInputAudioMonitoring = useCallback((enabled: boolean) => engine.setInputAudioMonitoring(enabled), [engine]);
  return {
    inputAudioMonitoring,
    setInputAudioMonitoring
  };
}

/**
 * Selected input track state and controls.
 */
export interface SelectedInputChannelResult {
  selectedInputChannel: 'left' | 'right' | 'stereo';
  setSelectedInputChannel: (channel: 'left' | 'right' | 'stereo') => void;
}

/**
 * Hook for managing the selected input channel.
 * Only applicable when the input device has multiple tracks.
 *
 * @example
 * ```tsx
 * const { selectedInputChannel, setSelectedInputChannel } = useSelectedInputChannel();
 *
 * // User selects a channel
 * setSelectedInputChannel('left');
 * ```
 */
export function useSelectedInputChannel(): SelectedInputChannelResult {
  const engine = useAudioEngine();
  const selectedInputChannel = useAudioEngineState(s => s.selectedInputChannel);
  const setSelectedInputChannel = useCallback((channel: 'left' | 'right' | 'stereo') => engine.setSelectedInputChannel(channel), [engine]);
  return {
    selectedInputChannel,
    setSelectedInputChannel
  };
}

/**
 * Hook for checking if a track has any pending regions (recorded but not uploaded).
 *
 * @example
 * ```tsx
 * const isPending = useTrackPending('track-1');
 * if (isPending) {
 *   // Show pending indicator
 * }
 * ```
 */
export function useTrackPending(trackId: string): boolean {
  return useAudioEngineState((_s, e) => e.hasTrackPendingRegions(trackId));
}

/**
 * Hook for pending region controls.
 */
export interface PendingRegionControls {
  removeTrack: (id: string) => void;
  getPendingBlob: (regionId: string) => Blob | undefined;
}

/**
 * Hook for managing pending regions and tracks.
 *
 * @example
 * ```tsx
 * const { removeTrack, getPendingBlob } = usePendingRegionControls();
 *
 * const handleDiscard = () => {
 *   removeTrack(trackId);
 * };
 * ```
 */
export function usePendingRegionControls(): PendingRegionControls {
  const engine = useAudioEngine();
  return {
    removeTrack: useCallback((id: string) => engine.removeTrack(id), [engine]),
    getPendingBlob: useCallback((regionId: string) => engine.getPendingBlob(regionId), [engine])
  };
}

/**
 * Hook for getting regions for a specific track.
 *
 * @example
 * ```tsx
 * const regions = useTrackRegions('track-1');
 * regions.map(region => <Region key={region.id} region={region} />);
 * ```
 */
export function useTrackRegions(trackId: string): RegionState[] {
  return useAudioEngineState((_s, e) => e.getRegionsForTrack(trackId));
}

/**
 * Hook for getting ordered track IDs.
 * Returns stable string array that only changes when tracks are added/removed/reordered.
 * Does not re-render when track properties (mute/solo/volume) change.
 *
 * @example
 * ```tsx
 * const trackIds = useOrderedTrackIds();
 * trackIds.map(id => <Track key={id} trackId={id} />);
 * ```
 */
export function useOrderedTrackIds(): string[] {
  return useAudioEngineState(s => Object.values(s.tracks).sort((a, b) => a.order - b.order).map(c => c.id));
}